import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { LineReveal } from "@/components/shared/Reveal";
import { EcosystemFlow } from "./EcosystemFlow";

const fade = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative isolate min-h-[100svh] overflow-hidden pt-[76px]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-20">
        <img src={IMAGES.truck} alt="" className="h-[120%] w-full object-cover object-center opacity-60" loading="eager" />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy via-transparent to-navy/60" />
      <div className="absolute inset-0 -z-10 grid-bg opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="grain absolute inset-0 -z-10" />

      <motion.div style={{ y: contentY }} className="wrap relative grid min-h-[calc(100svh-76px)] items-center gap-16 py-20 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.1)} className="flex items-center gap-3">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-pulse_ring rounded-full bg-cyan-400" /><span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" /></span>
            <span className="eyebrow">Transportation Technology Systems Integrator · Dubai, UAE</span>
          </motion.div>

          <LineReveal
            lines={["Transportation", "Technology.", "Integrated."]}
            delay={0.25}
            className="mt-8 text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
          />

          <motion.p {...fade(0.75)} data-testid="hero-subheadline" className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            Wexoz designs, integrates and deploys connected transportation solutions — from IoT fleet management and
            logistics automation to CCTV, school transport and AI-powered operations.
          </motion.p>
          <motion.p {...fade(0.85)} className="mt-4 max-w-xl font-mono text-xs uppercase tracking-[0.18em] text-slate-500">
            One technology partner. Multiple platforms. Hardware, software, connectivity and intelligence working together.
          </motion.p>

          <motion.div {...fade(0.95)} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-talk-to-expert-cta" className="btn-primary">
              Talk to an Expert <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/solutions" data-testid="hero-explore-solutions-cta" className="btn-ghost group">
              Explore Our Solutions <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <motion.div {...fade(0.6)} className="lg:col-span-5">
          <EcosystemFlow />
        </motion.div>
      </motion.div>

      <motion.div {...fade(1.4)} className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 md:flex">
        <span className="h-px w-10 bg-line-strong" /> Scroll <span className="h-px w-10 bg-line-strong" />
      </motion.div>
    </section>
  );
};
